import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Loader2, Wallet, ArrowUpRight, ArrowDownLeft, User, Building2,
  ShieldCheck, Briefcase, Mail, Phone, CreditCard, TrendingUp, Store,
  Calendar, Hash
} from 'lucide-react';
import { toast } from 'sonner';
import walletApi from '../../lib/walletApi';

const fmt = (n) => `NGN ${Number(n || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;
const TYPE_COLORS = {
  super_admin: 'bg-pink-500', company_admin: 'bg-blue-500', employee: 'bg-emerald-500'
};

function InfoRow({ icon: Icon, label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
      <Icon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
      <span className="text-[11px] text-gray-500 w-28 shrink-0">{label}</span>
      <span className="text-[11px] text-gray-900 font-medium">{value}</span>
    </div>
  );
}

export default function UserDetailPage() {
  const { userType, userId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await walletApi.get(`/super-admin/user-detail/${userType}/${userId}`).then(r => r.data);
        setData(res);
      } catch (e) {
        toast.error('Failed to load user details');
      } finally { setLoading(false); }
    };
    load();
  }, [userType, userId]);

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-pink-400" />
    </div>
  );
  if (!data) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <p className="text-gray-400">User not found</p>
    </div>
  );

  const p = data.profile;
  const w = data.wallet;
  const txns = data.transactions || [];
  const elections = data.elections || [];
  const topVendors = data.top_vendors || [];
  const summary = data.transaction_summary || {};

  return (
    <div className="min-h-screen bg-gray-50" data-testid="user-detail-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-5">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl ${TYPE_COLORS[p.user_type] || 'bg-gray-500'} flex items-center justify-center text-white text-lg font-bold`}>
              {(p.first_name?.[0] || '')}{(p.last_name?.[0] || '')}
            </div>
            <div className="flex-1">
              <h1 className="text-lg font-bold text-gray-900">{p.first_name} {p.last_name}</h1>
              <p className="text-sm text-gray-500">{p.email}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase text-white ${TYPE_COLORS[p.user_type]}`}>
                  {p.user_type?.replace('_', ' ')}
                </span>
                {p.company_name && <span className="text-[10px] text-gray-400">{p.company_name}</span>}
                {p.status && <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${p.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>{p.status}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        {/* Top cards */}
        {w && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Wallet className="w-4 h-4 text-pink-500" />
                  <span className="text-[10px] font-semibold text-gray-400 uppercase">Wallet Balance</span>
                </div>
                <p className="text-xl font-bold text-gray-900" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="wallet-balance">
                  {fmt(w.balance)}
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">Available: {fmt(w.available_balance)}</p>
              </CardContent>
            </Card>
            <Card className="border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <ArrowUpRight className="w-4 h-4 text-red-400" />
                  <span className="text-[10px] font-semibold text-gray-400 uppercase">Total Sent</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{summary.total_sent}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">Volume: {fmt(summary.total_volume)}</p>
              </CardContent>
            </Card>
            <Card className="border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Store className="w-4 h-4 text-violet-500" />
                  <span className="text-[10px] font-semibold text-gray-400 uppercase">Vendor Usage</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{topVendors.length}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">vendors used</p>
              </CardContent>
            </Card>
            <Card className="border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-blue-500" />
                  <span className="text-[10px] font-semibold text-gray-400 uppercase">Benefit Elections</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{elections.length}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">active elections</p>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left column — Profile + Top Vendors */}
          <div className="space-y-4">
            <Card className="border-gray-200">
              <CardHeader className="pb-2"><CardTitle className="text-xs font-semibold text-gray-600">Profile Details</CardTitle></CardHeader>
              <CardContent className="px-4 pb-4">
                <InfoRow icon={Mail} label="Email" value={p.email} />
                <InfoRow icon={Phone} label="Phone" value={p.phone} />
                <InfoRow icon={Briefcase} label="Department" value={p.department} />
                <InfoRow icon={Briefcase} label="Job Title" value={p.job_title} />
                <InfoRow icon={Building2} label="Employment" value={p.employment_type} />
                <InfoRow icon={Hash} label="Salary" value={p.gross_salary ? fmt(p.gross_salary) : null} />
                <InfoRow icon={CreditCard} label="Bank" value={p.bank_name ? `${p.bank_name} • ${p.account_number}` : null} />
                <InfoRow icon={ShieldCheck} label="BVN" value={p.has_bvn ? 'Verified' : 'Not set'} />
                <InfoRow icon={User} label="Gender" value={p.gender} />
                <InfoRow icon={Calendar} label="DOB" value={p.date_of_birth} />
                <InfoRow icon={Calendar} label="Joined" value={p.created_at?.slice(0, 10)} />
              </CardContent>
            </Card>

            {/* Top Vendors */}
            {topVendors.length > 0 && (
              <Card className="border-gray-200">
                <CardHeader className="pb-2"><CardTitle className="text-xs font-semibold text-gray-600">Top Vendors</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-[11px]">
                    <thead className="bg-gray-50"><tr>
                      <th className="text-left py-2 px-3 font-semibold text-gray-500">Vendor</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-500">Spent</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-50">
                      {topVendors.map((v, i) => (
                        <tr key={i}>
                          <td className="py-2 px-3">
                            <p className="font-medium text-gray-900">{v.name}</p>
                            <p className="text-[9px] text-gray-400 capitalize">{v.category?.replace(/_/g, ' ')}</p>
                          </td>
                          <td className="py-2 px-3 text-right font-semibold text-gray-700">{fmt(v.total_spent)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right column — Transactions + Elections */}
          <div className="lg:col-span-2 space-y-4">
            {/* Transactions */}
            <Card className="border-gray-200">
              <CardHeader className="pb-2"><CardTitle className="text-xs font-semibold text-gray-600">Transaction History</CardTitle></CardHeader>
              <CardContent className="p-0">
                {txns.length > 0 ? (
                  <table className="w-full text-[11px]">
                    <thead className="bg-gray-50"><tr>
                      <th className="text-left py-2 px-3 font-semibold text-gray-500">Type</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-500 hidden sm:table-cell">Description</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-500">Amount</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-500">Status</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-500 hidden md:table-cell">Date</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-50">
                      {txns.map(t => (
                        <tr key={t.id}>
                          <td className="py-2 px-3">
                            <div className="flex items-center gap-1.5">
                              {t.direction === 'in' ? (
                                <ArrowDownLeft className="w-3.5 h-3.5 text-emerald-500" />
                              ) : (
                                <ArrowUpRight className="w-3.5 h-3.5 text-red-400" />
                              )}
                              <span className="capitalize text-gray-700">{t.type?.replace(/_/g, ' ')}</span>
                            </div>
                          </td>
                          <td className="py-2 px-3 text-gray-500 hidden sm:table-cell max-w-[180px] truncate">{t.narration || t.description || '—'}</td>
                          <td className="py-2 px-3 text-right font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace', color: t.direction === 'in' ? '#10b981' : '#111827' }}>
                            {t.direction === 'in' ? '+' : '-'}{fmt(t.amount)}
                          </td>
                          <td className="py-2 px-3">
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${
                              t.status === 'completed' ? 'bg-emerald-50 text-emerald-600' :
                              t.status === 'pending' ? 'bg-amber-50 text-amber-600' :
                              'bg-red-50 text-red-600'
                            }`}>{t.status}</span>
                          </td>
                          <td className="py-2 px-3 text-gray-400 hidden md:table-cell">{t.created_at?.slice(0, 16).replace('T', ' ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-center text-xs text-gray-400 py-8">No transactions yet</p>
                )}
              </CardContent>
            </Card>

            {/* Benefit Elections */}
            {elections.length > 0 && (
              <Card className="border-gray-200">
                <CardHeader className="pb-2"><CardTitle className="text-xs font-semibold text-gray-600">Benefit Elections</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-[11px]">
                    <thead className="bg-gray-50"><tr>
                      <th className="text-left py-2 px-3 font-semibold text-gray-500">Category</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-500">Vendor</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-500">Amount</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-500">Status</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-50">
                      {elections.map(e => (
                        <tr key={e.id}>
                          <td className="py-2 px-3 capitalize text-gray-700">{e.category?.replace(/_/g, ' ')}</td>
                          <td className="py-2 px-3 text-gray-500">{e.vendor_name || '—'}</td>
                          <td className="py-2 px-3 text-right font-semibold text-gray-700">{fmt(e.amount)}</td>
                          <td className="py-2 px-3">
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${
                              e.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'
                            }`}>{e.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
